import type { PickedFiles } from './folder-picker';

/**
 * What was dragged onto the start screen or the spine, waiting for the picker it stands in for.
 * A drop starts the same "Add pages" the source cards do (`acquireDocuments('files' | 'folder')`);
 * the renderer then asks the desktop bridge for its picker, and the adapter's picker answers with
 * this instead of opening the browser's dialog (folder-picker.ts). Taken once: the next click on
 * Add pages opens the dialog again. A drop nobody asked for within a few seconds (the screen was
 * busy) is forgotten so it cannot answer a later click.
 */
export type DroppedPages =
  { source: 'files'; files: File[] } | { source: 'folder'; picked: PickedFiles };

const freshFor = 10_000;
let waiting: { pages: DroppedPages; at: number } | null = null;

export function offerDrop(pages: DroppedPages): void {
  waiting = { pages, at: Date.now() };
}

/** The dropped files, if loose files were dropped; once. */
export function takeDroppedFiles(): File[] | null {
  const pages = take('files');
  return pages?.source === 'files' ? pages.files : null;
}

/** The dropped folder, sorted as the picker sorts one; once. */
export function takeDroppedFolder(): PickedFiles | null {
  const pages = take('folder');
  return pages?.source === 'folder' ? pages.picked : null;
}

function take(source: DroppedPages['source']): DroppedPages | null {
  const held = waiting;
  if (!held || Date.now() - held.at > freshFor) {
    waiting = null;
    return null;
  }
  if (held.pages.source !== source) return null;
  waiting = null;
  return held.pages;
}
